/**
 * USE-WORK-ORDER-FILE-UPLOAD.JS - Work Order File Upload Management Hook
 * 
 * SUMMARY:
 * Custom hook that manages file upload functionality for work orders.
 * Handles file validation, upload state, and file removal for both new and existing attachments.
 */

import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { errorSnackBar } from '../../../../actions/common.action';
import { fileValidator } from '../../../../util/validators/file';

/**
 * Custom hook for managing work order file uploads.
 * 
 * @param {Object} poDataRef - Reference to existing PO data (for editing)
 * @returns {Object} File upload state and handlers
 * 
 * @example
 * const {
 *   fileToUpload,
 *   removedFiles,
 *   handleFileUpload,
 *   handleRemoveFile
 * } = useWorkOrderFileUpload(poDataRef);
 */
export const useWorkOrderFileUpload = (poDataRef = { current: null }) => {
  const dispatch = useDispatch();
  const [fileToUpload, setFileToUpload] = useState([]);
  const [removedFiles, setRemovedFiles] = useState([]);

  /**
   * Handles file upload with validation.
   * Validates file size (max 10MB) and file type.
   * Supports multiple file upload (max 5 files at a time).
   * 
   * @param {Event} e - File input change event
   * 
   * VALIDATION:
   * - Maximum 5 files per upload
   * - Maximum file size: 10MB
   * - Validates file type using fileValidator
   */
  const handleFileUpload = (e) => {
    let isValid = true;

    if (e.target.files.length > 5) {
      dispatch(errorSnackBar('You can not upload more than 5 files at a time'));
      return;
    }

    const files = e.target.files;

    Array.from(files).forEach(file => {
      const fileValidation = fileValidator({ file, acceptedFileSize: 10 });
      const { validFileSize, validFileType, error } = fileValidation;

      if (!validFileSize.valid && !error) {
        isValid = false;
        dispatch(errorSnackBar(validFileSize.error));
      } else if (!validFileType.valid && !error) {
        isValid = false;
        dispatch(errorSnackBar(validFileType.error));
      } else if (error) {
        isValid = false;
        dispatch(errorSnackBar(error.error));
      }
    });

    if (!isValid) return;

    setFileToUpload(prev => [...prev, ...Array.from(files)]);
    e.target.value = '';
  };

  /**
   * Removes a newly uploaded file from the upload queue.
   * 
   * @param {number} index - Index of file to remove from fileToUpload array
   */
  const handleRemoveFile = (index) => {
    setFileToUpload(prev => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  };

  /**
   * Removes an existing file attachment from the work order.
   * Tracks removal for backend processing on form submission.
   * 
   * @param {string} key - S3 key of existing file to remove
   */
  const handleRemoveExistingFile = (key) => {
    if (poDataRef.current && Array.isArray(poDataRef.current.attachment)) {
      const updatedAttachments = poDataRef.current.attachment.filter(
        file => file.key !== key
      );
      poDataRef.current.attachment = updatedAttachments;
    }

    setRemovedFiles(prev => [...prev, key]);
  };

  return {
    fileToUpload,
    removedFiles,
    handleFileUpload,
    handleRemoveFile,
    handleRemoveExistingFile,
    setFileToUpload,
  };
};
